import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type MouseEvent, type ReactNode, useId, useState } from "react";

const Actions = Resources.AnalysisDetail.Goals.Timeline.Actions;

export type GoalActionTone = "default" | "danger" | "on";

const ICON_COLORS: Record<GoalActionTone, string> = {
  default: "text.secondary",
  danger: "error.main",
  on: "success.dark",
};

export type GoalActionItem = {
  key: string;
  text: typeof Actions.Archive;
  icon: ReactNode;
  tone: GoalActionTone;
  onClick: () => void;
};

type Props = {
  actions: GoalActionItem[];
  disabled: boolean;
};

export const GoalActionsMenu = ({ actions, disabled }: Props) => {
  const { t } = useResource();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-label={t(Actions.More)}
        aria-controls={anchorEl ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        sx={{ width: 32, height: 32, borderRadius: Radius.M, color: "text.secondary" }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            elevation: 3,
            sx: { mt: Spacing.S, border: "1px solid", borderColor: "divider", borderRadius: Radius.M },
          },
        }}
      >
        {actions.map(({ key, text, icon, tone, onClick }) => (
          <MenuItem
            key={key}
            disabled={disabled}
            onClick={() => {
              setAnchorEl(null);
              onClick();
            }}
            sx={{ py: Spacing.L, px: Spacing.XL, minHeight: "auto", color: tone === "danger" ? "error.main" : "text.primary" }}
          >
            <ListItemIcon sx={{ color: ICON_COLORS[tone], "& .MuiSvgIcon-root": { fontSize: 20 } }}>{icon}</ListItemIcon>
            <ListItemText primary={t(text.Title)} slotProps={{ primary: { fontSize: 14 } }} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
